import {
  SupportCreateTicket,
  SupportTicketCategory,
  SupportTicketChoice,
  SupportTicketSubcategory,
  SupportTicketSubcategoryChoice,
} from '../../../types/commonTypes';
import { ChangeEvent, MutableRefObject, useEffect, useState } from 'react';
import { Select, SelectItem } from '../../../components/Select';
import { Input } from '../../../components/Input';

interface Props {
  categories: SupportTicketCategory[];
  loading: boolean;
  updateFormField(key: keyof SupportCreateTicket, value: any): void;
  ticketFields: SupportCreateTicket;
  scrollRef: MutableRefObject<any>;
}

export const TicketSubCategories = ({
  categories,
  loading,
  updateFormField,
  ticketFields,
  scrollRef,
}: Props) => {
  const [fields, setFields] = useState<SupportTicketSubcategory[]>([]);
  const [ticketChoices, setTicketChoices] = useState<SupportTicketChoice[]>([]);
  const category =
    categories.find((c) => c.id === ticketFields.category) ?? null;

  useEffect(() => {
    updateFormField('subcategories', ticketChoices);
  }, [ticketChoices]);

  const resetFields = (
    subCategoryId: number,
    choice?: SupportTicketSubcategoryChoice
  ) => {
    const fieldIndex = fields.findIndex((f) => f.id === subCategoryId);
    const choiceSubCategories = choice ? choice.subcategories : null;

    if (fieldIndex !== -1 && choiceSubCategories) {
      setFields([...fields.slice(0, fieldIndex + 1), ...choiceSubCategories]);
    }
  };

  const validateChoice = (
    subCategoryId: number,
    value: string | number,
    index: number,
    choice?: SupportTicketSubcategoryChoice
  ) => {
    const matchedSubCategory = ticketChoices.find(
      (c) => c.id === subCategoryId
    );

    if (matchedSubCategory) {
      var matchedIndex = ticketChoices.indexOf(matchedSubCategory);
      if (typeof matchedSubCategory.value === 'string') {
        ticketChoices[matchedIndex].value = value;
        setTicketChoices([...ticketChoices]);
      } else {
        setTicketChoices([
          ...ticketChoices.slice(0, matchedIndex),
          { id: subCategoryId, value: value },
        ]);
        resetFields(subCategoryId, choice);
      }
    } else {
      setTicketChoices([
        ...ticketChoices.slice(0, index),
        { id: subCategoryId, value: value },
        ...ticketChoices.slice(index),
      ]);

      if (choice && choice.subcategories)
        setFields((fields) => [...fields, ...choice.subcategories]);
    }
  };

  const handleInputChange = (
    event: ChangeEvent<any>,
    subCategory: SupportTicketSubcategory,
    index: number
  ) => {
    validateChoice(subCategory.id, event.target.value, index);
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSelectChange = (
    event: ChangeEvent<any>,
    subCategory: SupportTicketSubcategory,
    index: number
  ) => {
    const subCategoryChoice = subCategory.choices.find(
      (choice) => choice.id === +event.target.value
    );

    if (subCategoryChoice) {
      validateChoice(
        subCategory.id,
        +event.target.value,
        index,
        subCategoryChoice
      );
    }
  };

  useEffect(() => {
    if (category) {
      setFields(category.subcategories);
      setTicketChoices([]);
    }
  }, [ticketFields.category]);

  const renderTextField = (
    subCategory: SupportTicketSubcategory,
    index: number
  ) => {
    return (
      <Input
        key={`${subCategory.name}-input`}
        color="primary"
        type="text"
        placeholder={subCategory.name}
        required={subCategory.required}
        fullWidth
        value={ticketChoices.find((t) => t.id === subCategory.id)?.value ?? ''}
        onChange={(e) => handleInputChange(e, subCategory, index)}
        variant="contained"
        disabled={loading}
      />
    );
  };

  const renderSelectField = (
    subCategory: SupportTicketSubcategory,
    index: number
  ) => {
    return (
      <Select
        key={`${subCategory.name}-select`}
        className="col-span-full"
        label={subCategory.name}
        fullWidth
        value={ticketChoices.find((t) => t.id === subCategory.id)?.value ?? ''}
        required={subCategory.required}
        onChange={(e) => handleSelectChange(e, subCategory, index)}
        variant="contained"
        disabled={loading}
      >
        {subCategory.choices.map((choice) => (
          <SelectItem
            key={`${subCategory.name}-choice-${choice.id}`}
            id={`${subCategory.id}`}
            value={choice.id}
          >
            {choice.text}
          </SelectItem>
        ))}
      </Select>
    );
  };

  return (
    <div className="flex flex-col items-center justify-around gap-2 w-full max-w-[500px] my-6">
      {fields.map((field, index) => {
        if (field.type.toLowerCase() === 'choice')
          return renderSelectField(field, index);
        else if (
          field.type.toLowerCase() === 'text' ||
          field.type.toLowerCase() === 'number'
        )
          return renderTextField(field, index);
      })}
    </div>
  );
};
